/**
 * Avatar trigger + popover for re-assigning a MemoForm's owner (notes and
 * tasks alike). The trigger wears `MEMO_ICON_BUTTON_CLASS`, the shared size-6
 * box every memo icon button uses, so it lines up with the privacy toggle it
 * sits beside in the controls cluster.
 *
 * The trigger uses a react-aria `Button` (aliased as AriaButton) —
 * DialogTrigger can wire it as a press source; we can't wrap it in
 * `<Tooltip>` because both react-aria triggers want to claim the same child
 * element. The option list uses react-aria `ListBox`/`ListBoxItem` (not
 * `@/lib/base` `Button`, whose centered-pill styling and `!important`
 * padding don't fit a left-aligned option row) — this gives arrow-key
 * roving-tabindex navigation and typeahead for free.
 *
 * See `task/OrgMemberPicker.tsx` for a searchable sibling of this picker
 * (used by the task footer's "Reassign" and the inline Assignee row) — the
 * two are kept as a deliberate small duplication rather than merged, since
 * `OrgMemberPicker` adds a search field this compact popover intentionally
 * doesn't have. The member-display helpers below are shared from
 * `./memoMembers` so they can't drift between the two.
 */

import type { Membership } from 'api-client';
import { useCallback, useMemo, useState } from 'react';
import {
  Button as AriaButton,
  Dialog,
  DialogTrigger,
  ListBox,
  ListBoxItem,
} from 'react-aria-components';

import { Avatar } from '@/lib/base/avatar/avatar';
import { Popover } from '@/lib/base/overlays/popover';
import { useCrmObjectSnapshots } from '@/lib/crm/hooks';
import { cx } from '@/utils/cx';

import { MEMO_ICON_BUTTON_CLASS } from './memoIconButton';
import {
  deriveInitials,
  filterAssignableMembers,
  getMemberAvatarSrc,
  memberDisplayName,
} from './memoMembers';

// Panel chrome lives on the lib/base Popover (which also owns outside-click
// dismissal); the Dialog keeps the size cap + scroll. `py-0` drops the
// wrapper's list padding (the Dialog pads itself) and `w-auto min-w-56`
// replace its trigger-width sizing (the avatar trigger is only 24px wide).
const POPOVER_CLASS = 'w-auto min-w-56 max-w-72 py-0 shadow-xl';
const DIALOG_CLASS = 'max-h-72 overflow-y-auto p-1 outline-hidden';

const LIST_ITEM_CLASS =
  'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none cursor-pointer transition-colors';

interface UserOptionProps {
  user: Membership;
  isSelected: boolean;
  src: string | undefined;
}

function UserOption({ user, isSelected, src }: UserOptionProps) {
  return (
    <ListBoxItem
      id={user.entityId}
      textValue={memberDisplayName(user)}
      // The list deliberately has NO react-aria selectionMode (see the
      // ListBox below), so the current owner is conveyed by the sr-only
      // suffix in the row content (ListBoxItem does not forward custom
      // aria-* props); the data attribute is the styling/testing hook.
      data-current-owner={isSelected ? 'true' : undefined}
      className={({ isFocused }) =>
        cx(
          LIST_ITEM_CLASS,
          isSelected
            ? 'bg-primary_hover text-primary'
            : 'text-secondary hover:bg-primary_hover',
          isFocused && !isSelected && 'bg-primary_hover'
        )
      }
    >
      <Avatar
        size='xs'
        src={src}
        initials={deriveInitials(user)}
        alt={memberDisplayName(user)}
      />
      <span className='flex-1 truncate'>{memberDisplayName(user)}</span>
      {isSelected && <span className='sr-only'>(current owner)</span>}
    </ListBoxItem>
  );
}

export interface MemoOwnerPickerProps {
  users: Membership[];
  ownerMember: Membership | undefined;
  orgId: number | undefined;
  onSelect: (entityId: string) => void;
  isDisabled?: boolean;
}

/** Avatar trigger + popover for re-assigning the note owner. See file header. */
export function MemoOwnerPicker({
  users,
  ownerMember,
  orgId,
  onSelect,
  isDisabled,
}: MemoOwnerPickerProps) {
  // Own the popover's open state so the avatar fetch below can be gated on it.
  // Outside-click dismissal (including trigger re-click-to-close) belongs to
  // the lib/base Popover, which closes through DialogTrigger's onOpenChange.
  const [isOpen, setIsOpen] = useState(false);
  const ownerLabel = ownerMember
    ? `Owner: ${memberDisplayName(ownerMember)}`
    : 'Assign owner';

  // Exclude viewers and inactive/partner members from the selectable list —
  // mirrors OrgMemberPicker and the backend's ASSIGNABLE_Q. A viewer who
  // already owns this note still shows in the trigger (ownerMember / ownerAvatarSrc
  // remain unfiltered); only the dropdown options and their avatar batch-fetch
  // are filtered.
  const assignableUsers = useMemo(
    () => filterAssignableMembers(users),
    [users]
  );
  // Batch-fetch avatar snapshots. While CLOSED, fetch only the current owner —
  // that's the single avatar the trigger renders, so a mounted-but-unopened
  // picker on a large org must not fetch a CRM object per assignable member (an
  // unnecessary request + store-subscription fan-out). While OPEN, fetch the
  // full assignable set (the option-list avatars) PLUS the current owner — a
  // legacy note owned by a viewer/partner/deactivated member falls outside
  // assignableUsers, so without it that avatar would never load in the trigger.
  // The owner is added to the fetch id set only; the selectable option list
  // stays filtered to assignableUsers.
  const memberEntityIds = useMemo(() => {
    const ids = isOpen ? assignableUsers.map(u => u.entityId) : [];
    const ownerId = ownerMember?.entityId;
    if (ownerId && !ids.includes(ownerId)) {
      ids.push(ownerId);
    }
    return ids;
  }, [isOpen, assignableUsers, ownerMember?.entityId]);
  const memberObjects = useCrmObjectSnapshots(memberEntityIds, orgId);

  const ownerAvatarSrc = ownerMember
    ? getMemberAvatarSrc(memberObjects.get(ownerMember.entityId))
    : undefined;

  const handleAction = useCallback(
    (key: React.Key) => onSelect(String(key)),
    [onSelect]
  );

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      {/* react-aria-components `Button` (aliased as AriaButton) instead
          of a plain <button>: DialogTrigger only wires its press source
          when the child is a react-aria pressable. A plain HTML button
          renders fine but no-ops on click — the popover never opens.
          Avoid lib/base `Button`; that variant wraps non-icon children
          in a `<span data-text px-0.5>` slot whose `text-sm`/baseline
          metrics push the avatar off-center horizontally.
          The avatar is geometrically centered (no vertical nudge) to match
          the privacy icon button it sits beside in the controls cluster —
          both are size-4 content centered in an identical size-6 button, so
          they share one centerline. */}
      <AriaButton
        aria-label={ownerLabel}
        isDisabled={isDisabled}
        className={MEMO_ICON_BUTTON_CLASS}
      >
        <span className='flex items-center justify-center'>
          {ownerMember ? (
            <Avatar
              size='xxs'
              src={ownerAvatarSrc}
              initials={deriveInitials(ownerMember)}
              alt={memberDisplayName(ownerMember)}
            />
          ) : (
            <span
              aria-hidden
              className='inline-block size-4 rounded-full bg-bg-tertiary'
            />
          )}
        </span>
      </AriaButton>
      {/* lib/base Popover: non-modal, outside-click dismissal, portal event
          boundary and stacking z-index are all owned by the wrapper. */}
      <Popover
        size='md'
        placement='bottom end'
        offset={4}
        shouldFlip
        containerPadding={16}
        className={POPOVER_CLASS}
      >
        <Dialog aria-label='Assign owner' className={DIALOG_CLASS}>
          {({ close }) => (
            // Deliberately NO react-aria selectionMode: this is an ACTION
            // list, not a selection list — see OrgMemberPicker's ListBox
            // for the same reasoning (selectionMode + onAction + a
            // non-empty controlled selection demotes onAction). The
            // current owner is conveyed via UserOption's isSelected
            // styling + sr-only text instead.
            <ListBox
              aria-label='Owners'
              className='flex flex-col gap-0.5 outline-hidden'
              onAction={key => {
                handleAction(key);
                close();
              }}
            >
              {assignableUsers.map(user => (
                <UserOption
                  key={user.entityId}
                  user={user}
                  isSelected={user.entityId === ownerMember?.entityId}
                  src={getMemberAvatarSrc(memberObjects.get(user.entityId))}
                />
              ))}
            </ListBox>
          )}
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}
